import { useState } from 'react';
// MUI
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
// MUI ICONS
import Groups from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
// CUSTOM HOOK
import { useTeam } from '@/hooks/useTeam';

export default function SidebarTeamSelector({ compact = false }) {
  const { selectedTeam, clubsWithTeams, isLoading, selectTeam } = useTeam();
  const [open, setOpen] = useState(false);

  // Don't render if no teams available
  if (!isLoading && clubsWithTeams.length === 0) {
    return null;
  }

  const handleChange = (event) => {
    const teamId = event.target.value;
    // Find the team in clubsWithTeams
    for (const club of clubsWithTeams) {
      const team = club.teams.find((t) => t.id === teamId);
      if (team) {
        selectTeam(team);
        break;
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1.5,
          mb: 1,
        }}
      >
        <CircularProgress size={18} />
        {!compact && (
          <Typography variant="caption" color="text.secondary">
            Loading...
          </Typography>
        )}
      </Box>
    );
  }

  // Compact mode - show just an icon with tooltip
  if (compact) {
    return (
      <Tooltip title={selectedTeam?.name || 'Select Team'} placement="right">
        <Box
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 1.5,
            mb: 1,
            mx: 'auto',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            cursor: 'pointer',
            bgcolor: selectedTeam ? (theme) => alpha(theme.palette.primary.main, 0.1) : 'action.hover',
            color: selectedTeam ? 'primary.main' : 'text.secondary',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
            },
          }}
        >
          <Groups sx={{ fontSize: 20 }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          display: 'block',
          mb: 1,
          px: 1,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontSize: 11,
        }}
      >
        Active Team
      </Typography>
      <Select
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={selectedTeam?.id || ''}
        onChange={handleChange}
        displayEmpty
        size="small"
        fullWidth
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1.5,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
          },
          bgcolor: 'background.paper',
          borderRadius: 1.5,
        }}
        renderValue={(value) => {
          if (!value || !selectedTeam) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Groups sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Select Team
                </Typography>
              </Box>
            );
          }
          // Find club name for the selected team
          const club = clubsWithTeams.find((c) => c.teams.some((t) => t.id === value));
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
              <Groups sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
              <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{ lineHeight: 1.3 }}
                >
                  {selectedTeam.name}
                </Typography>
                {club && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ lineHeight: 1.2, display: 'block', fontSize: 10 }}
                  >
                    {club.name}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 350,
              mt: 0.5,
            },
          },
        }}
      >
        {clubsWithTeams.map((club) => [
          <ListSubheader
            key={`club-${club.id}`}
            sx={{
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 0,
              pb: 1,
              lineHeight: 1.5,
              fontWeight: 600,
              fontSize: 12,
              color: 'text.primary',
            }}
          >
            <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            {club.name || 'Unknown Club'}
          </ListSubheader>,
          ...club.teams.map((team) => (
            <MenuItem
              key={team.id}
              value={team.id}
              sx={{
                pl: 3.5,
                pt: 2,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-selected': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  },
                },
              }}
            >
              <Groups sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" fontSize={13}>
                {team.name}
              </Typography>
            </MenuItem>
          )),
        ])}
      </Select>
    </Box>
  );
}
